import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../utils/firebase';
import TeamNavbar from '../components/TeamNavbar';

const TeamHelp = () => {
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const auth = getAuth();
  const user = auth.currentUser;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user || !message.trim()) return;

    setLoading(true);
    try {
      const ref = collection(db, 'teamSupportTickets');
      await addDoc(ref, {
        uid: user.uid,
        email: user.email,
        userType: 'team',
        messages: [
          {
            from: 'user',
            text: message.trim(),
            timestamp: new Date().toISOString(),
          },
        ],
        createdAt: serverTimestamp(),
        status: 'open',
      });

      setSubmitted(true);
      setMessage('');
    } catch (error) {
      console.error('Error submitting ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-900">
        <TeamNavbar />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-gray-800 rounded-lg p-8 border border-gray-700">
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-white mb-4">Support Ticket Submitted</h2>
              <p className="text-gray-400 mb-6">
                We've received your request and will get back to you within 24 hours.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Submit Another Ticket
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <TeamNavbar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Team Support</h1>
            <p className="text-gray-400">Get help with your team account and trivia games</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* FAQ Section */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4">Frequently Asked Questions</h2>
              
              <div className="space-y-4">
                <div className="border-b border-gray-700 pb-4">
                  <h3 className="text-white font-semibold mb-2">How do I join a team?</h3>
                  <p className="text-gray-400 text-sm">
                    Go to your Profile page and use the "Join Team" option. You'll need the team code or invitation from your team captain.
                  </p>
                </div>

                <div className="border-b border-gray-700 pb-4">
                  <h3 className="text-white font-semibold mb-2">How do I find trivia games?</h3>
                  <p className="text-gray-400 text-sm">
                    Use the "Find Games" feature to discover upcoming trivia games near you. You can filter by location, date, and difficulty.
                  </p>
                </div>

                <div className="border-b border-gray-700 pb-4">
                  <h3 className="text-white font-semibold mb-2">How do team leaderboards work?</h3>
                  <p className="text-gray-400 text-sm">
                    Leaderboards track your team's performance across all games. Points are awarded based on your final score and ranking.
                  </p>
                </div>

                <div className="border-b border-gray-700 pb-4">
                  <h3 className="text-white font-semibold mb-2">What are team achievements?</h3>
                  <p className="text-gray-400 text-sm">
                    Achievements are badges earned for milestones like winning games, team size, and participation. Check your Profile to see your badges.
                  </p>
                </div>

                <div className="border-b border-gray-700 pb-4">
                  <h3 className="text-white font-semibold mb-2">How do I rate a game?</h3>
                  <p className="text-gray-400 text-sm">
                    After completing a game, you'll receive a QR code to rate your experience. This helps improve future games and host ratings.
                  </p>
                </div>

                <div>
                  <h3 className="text-white font-semibold mb-2">When will the swag store be available?</h3>
                  <p className="text-gray-400 text-sm">
                    The team swag store is coming in Q1 2026! You'll be able to purchase custom team merchandise with your team colors and logo.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4">Still Need Help?</h2>
              <p className="text-gray-400 mb-6">
                Can't find what you're looking for? Send us a message and we'll get back to you within 24 hours.
              </p>
              
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-white mb-2">Your Message</label>
                  <textarea
                    className="w-full p-3 bg-gray-700 text-white rounded border border-gray-600 focus:border-green-500 focus:outline-none"
                    rows="6"
                    placeholder="Describe your issue or question..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading || !message.trim()}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  {loading ? 'Submitting...' : 'Submit Support Ticket'}
                </button>
              </form>

              <div className="mt-6 p-4 bg-gray-700 rounded-lg">
                <h3 className="text-white font-semibold mb-2">Contact Information</h3>
                <div className="text-gray-400 text-sm space-y-1">
                  <p>📧 Email: support@triviaplug.com</p>
                  <p>📱 Phone: Available through support tickets</p>
                  <p>⏰ Response Time: Within 24-48 hours</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamHelp; 